import { useMemo } from 'react';
import { DatabaseEvent } from '@/types/event';
import { useDatabaseEventsStore } from '@/stores';
import {
    EventsDataResult,
    mergeLiveDatabaseEvents,
    useSelectedSourceIds,
} from './eventsDataShared';
import {
    UseDatabaseEventsQueryOptions,
    useDatabaseEventsQuery,
} from './useDatabaseEventsQuery';
import { useInfiniteList } from './useInfiniteList';

export interface UseDatabaseEventsDataOptions extends UseDatabaseEventsQueryOptions {
    refreshInterval?: number;
}

export function useDatabaseEventsData({
    refreshInterval = 0,
    ...queryOptions
}: UseDatabaseEventsDataOptions = {}): EventsDataResult<DatabaseEvent> {
    const liveEvents = useDatabaseEventsStore(
        (state) => state.recentDatabaseEvents,
    );
    const { cacheKey, pageStateKey, filterParams, fetchPage } =
        useDatabaseEventsQuery(queryOptions);
    const selectedSourceIds = useSelectedSourceIds(queryOptions.sourceId);

    const result = useInfiniteList<DatabaseEvent>({
        cacheKey,
        pageStateKey,
        fetchPage,
        revalidateFirstPage: refreshInterval > 0,
        refreshInterval,
    });

    const events = useMemo(
        () =>
            mergeLiveDatabaseEvents(result.fetched, liveEvents, {
                sourceId: selectedSourceIds,
                startTime: filterParams.startTime,
                endTime: filterParams.endTime,
            }),
        [result.fetched, liveEvents, selectedSourceIds, filterParams],
    );

    return { ...result, events };
}
